import "@/index.css";
import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import api from "@/lib/api";
import ScrollToTop from "@/components/ScrollToTop";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import CustomerDashboard from "@/pages/CustomerDashboard";
import SetPasswordPage from "@/pages/SetPasswordPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ComingSoonPage from "@/pages/ComingSoonPage";
import OrderDetail from "@/pages/OrderDetail";
import PaymentSuccess from "@/pages/PaymentSuccess";
import AccountSettings from "@/pages/AccountSettings";
import BlogPage from "@/pages/BlogPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import SmsTermsPage from "@/pages/SmsTermsPage";
import ClientAuthorizationPage from "@/pages/ClientAuthorizationPage";
import ShippingRefundPage from "@/pages/ShippingRefundPage";
import PartnerDisclosurePage from "@/pages/PartnerDisclosurePage";
import RxAuthorizationPage from "@/pages/RxAuthorizationPage";
import UnsubscribePage from "@/pages/UnsubscribePage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import InsuranceLandingPage from "@/pages/InsuranceLandingPage";
import PartnerApplyPage from "@/pages/PartnerApplyPage";
import EstimateLinkPage from "@/pages/EstimateLinkPage";
import { useParams } from "react-router-dom";

function InvoiceRedirect() {
  const { orderId } = useParams();
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/invoice`;
  if (typeof window !== "undefined") window.location.replace(url);
  return null;
}
import EstimateModal from "@/components/EstimateModal";
import MedNotFoundPopup from "@/components/MedNotFoundPopup";
import ShippingPromoPopup from "@/components/ShippingPromoPopup";
import { Toaster } from "@/components/ui/sonner";
import ChatBot from "@/components/ChatBot";
import HubLayout from "@/pages/hub/HubLayout";
import AdminLayout from "@/pages/admin/AdminLayout";
import VetLayout from "@/pages/vet/VetLayout";
import FulfillmentLayout from "@/pages/fulfillment/FulfillmentLayout";
import PartnerLayout from "@/pages/partner/PartnerLayout";

function AppContent() {
  const location = useLocation();
  const path = location.pathname;

  // Public estimate + invoice links win regardless of host. (/invoice/:orderId was
  // previously only registered inside this branch, so emailed invoice links 404'd.)
  // /rx/ is a veterinary clinic opening a one time authorization link. It must bypass the
  // host routing AND the US-only geo gate: most of these clinics are in Canada, and a clinic
  // that lands on a launching-soon page simply will not come back.
  if (path.startsWith("/estimate/") || path.startsWith("/invoice/") || path.startsWith("/rx/")
      || path.startsWith("/unsubscribe/")) {
    return (
      <AuthProvider>
        <Routes>
          <Route path="/estimate/:token" element={<EstimateLinkPage />} />
          <Route path="/invoice/:orderId" element={<InvoiceRedirect />} />
          <Route path="/rx/:token" element={<RxAuthorizationPage />} />
          <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    );
  }

  // Subdomain-based routing — bulletproof against Nginx config drift after certbot
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isLinxHost = host.startsWith("linx.");
  const isVetHost = host.startsWith("access086.");
  const isFulfillmentHost = host.startsWith("access089.");
  const isHubHost = host.startsWith("hub.");
  const isPartnerHost = host.startsWith("partners.");

  const isHub = isHubHost || path.startsWith("/hub");
  const isAdmin = isLinxHost || path.startsWith("/admin");
  const isVet = isVetHost || path.startsWith("/vet");
  const isFulfillment = isFulfillmentHost || path.startsWith("/fulfillment");
  const isPartner = isPartnerHost || path.startsWith("/partner");

  if (isHub) return <HubLayout />;
  if (isAdmin) return <AdminLayout />;
  if (isVet) return <VetLayout />;
  if (isFulfillment) return <FulfillmentLayout />;
  if (isPartner) return <PartnerLayout />;

  return (

      <AuthProvider>
        <PublicSite />
      </AuthProvider>

  );
}

function PublicSite() {
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [estimatePrefill, setEstimatePrefill] = useState(null);
  const trackLocation = useLocation();
  useEffect(() => {
    const handler = (e) => { setEstimatePrefill(e?.detail || null); setEstimateOpen(true); };
    window.addEventListener("open-estimate-modal", handler);
    return () => window.removeEventListener("open-estimate-modal", handler);
  }, []);
  // Log each public page view (city/country resolved server-side; no IP stored). Fire-and-forget.
  useEffect(() => {
    api.post("/track-visit", { path: trackLocation.pathname, referrer: document.referrer || "" }).catch(() => {});
  }, [trackLocation.pathname]);

  // Geo gate: full site is US-only for now; other countries see the launching-soon page.
  // undefined = checking, true = allowed, object = blocked (carries country for the message).
  const [gate, setGate] = useState(undefined);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    try {
      if (params.get("preview") === "1") localStorage.setItem("pawlinx_preview", "1");
      if (params.get("preview") === "0") localStorage.removeItem("pawlinx_preview");
    } catch (e) { /* ignore */ }
    let bypass = false;
    try { bypass = localStorage.getItem("pawlinx_preview") === "1"; } catch (e) { /* ignore */ }
    if (bypass) { setGate(true); return; }
    let cancelled = false;
    api.get("/geo-gate")
      .then(({ data }) => { if (!cancelled) setGate(data?.allowed === false ? data : true); })
      .catch(() => { if (!cancelled) setGate(true); }); // gate unreachable → don't lock the site
    return () => { cancelled = true; };
  }, []);

  if (gate === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paw-bg">
        <img src="/pawlinx-logo-nav.png" alt="PawLinx" className="h-8 w-auto opacity-50 animate-pulse" />
      </div>
    );
  }
  if (gate !== true) {
    return (
      <>
        <ComingSoonPage country={gate.country} countryCode={gate.country_code} />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  // Signed-in dashboard carries its own chrome (sidebar + top bar), so the marketing
  // navbar and footer are suppressed there.
  const bareChrome = trackLocation.pathname === "/dashboard";

  return (
    <>
      <div className={bareChrome ? "min-h-screen flex flex-col" : "min-h-screen flex flex-col bg-paw-bg"}>
        {!bareChrome && <Navbar />}
        <main className={bareChrome ? "flex-1" : "flex-1 pb-14 sm:pb-0"}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/dashboard" element={<CustomerDashboard />} />
            <Route path="/set-password" element={<SetPasswordPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/order/:orderId" element={<OrderDetail />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/account" element={<AccountSettings />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/sms-terms" element={<SmsTermsPage />} />
            <Route path="/client-authorization" element={<ClientAuthorizationPage />} />
            <Route path="/shipping-refund" element={<ShippingRefundPage />} />
            <Route path="/partner-disclosure" element={<PartnerDisclosurePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/for-insurance" element={<InsuranceLandingPage />} />
            <Route path="/for-insurance/apply" element={<PartnerApplyPage />} />
          </Routes>
        </main>
        {!bareChrome && <Footer />}
      </div>
      <Toaster position="top-right" richColors />
      <ChatBot />
      <EstimateModal open={estimateOpen} prefill={estimatePrefill} onClose={() => { setEstimateOpen(false); setEstimatePrefill(null); }} />
      <MedNotFoundPopup />
      <ShippingPromoPopup />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
